import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Contract } from '@gigdriver/domain';
import { getActiveContract, sendContractForSignature as sendContractForSignatureApi, getContractPdf as getContractPdfApi } from '@gigdriver/client';

export const useContractsStore = defineStore('contracts', () => {
  const currentContract = ref<Contract | null>(null);
  const isLoading = ref(false);
  const isSending = ref(false);
  const isDownloading = ref(false);
  const error = ref<string | null>(null);

  async function loadContract(orgId: string, gigId: string) {
    try {
      isLoading.value = true;
      error.value = null;
      currentContract.value = await getActiveContract(orgId, gigId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load contract';
    } finally {
      isLoading.value = false;
    }
  }

  // Snapshots the gig's current quote + the org's standing terms into a
  // Contract and emails the client a link to sign — no account or login
  // required on their end. Safe to call again after edits: the Cloud
  // Function reuses the existing not-yet-signed contract in place rather
  // than creating a duplicate.
  async function sendForSignature(orgId: string, gigId: string) {
    try {
      isSending.value = true;
      error.value = null;
      await sendContractForSignatureApi({ orgId, gigId });
      await loadContract(orgId, gigId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to send the contract for signature';
      throw err;
    } finally {
      isSending.value = false;
    }
  }

  // Returns a fresh short-lived signed URL for an already-signed contract's
  // stored PDF — never persisted, always minted on demand.
  async function downloadPdf(orgId: string, gigId: string, contractId: string): Promise<string> {
    try {
      isDownloading.value = true;
      error.value = null;
      const { url } = await getContractPdfApi({ orgId, gigId, contractId });
      return url;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to download the contract PDF';
      throw err;
    } finally {
      isDownloading.value = false;
    }
  }

  return {
    currentContract,
    isLoading,
    isSending,
    isDownloading,
    error,
    loadContract,
    sendForSignature,
    downloadPdf,
  };
});
